import type { FeedbackCollectorConfig } from '@/types/feedbackCollector';
import type { IntercomConfig } from '@/types/intercom';

let cachedFeedbackCollectorConfig: FeedbackCollectorConfig | null = null;
let cachedIntercomConfig: IntercomConfig | null = null;

export const configService = {
  async getFeedbackCollectorConfig(): Promise<FeedbackCollectorConfig> {
    if (cachedFeedbackCollectorConfig) {
      return cachedFeedbackCollectorConfig;
    }

    const envToken = import.meta.env?.VITE_FEEDBACK_COLLECTOR_ACCOUNT_TOKEN;

    const defaultFeedbackCollectorConfig: FeedbackCollectorConfig = {
      apiHost: 'https://api.sentiment.accessacloud.com',
      accountToken: envToken ?? '',
      product: 'Projects',
    };

    try {
      const response = await fetch('/config/feedbackCollector.config.json');
      if (!response.ok) {
        console.warn('Feedback collector config not found, using defaults');
        return defaultFeedbackCollectorConfig;
      }

      const configFromFile = await response.json();
      // Merge config from file; env var overrides only if explicitly set
      cachedFeedbackCollectorConfig = {
        ...configFromFile,
        ...(envToken ? { accountToken: envToken } : {}),
      };
      return cachedFeedbackCollectorConfig!;
    } catch (error) {
      console.error('Failed to load feedback collector config:', error);
      return defaultFeedbackCollectorConfig;
    }
  },

  async getIntercomConfig(): Promise<IntercomConfig> {
    if (cachedIntercomConfig) {
      return cachedIntercomConfig;
    }

    const defaultIntercomConfig: IntercomConfig = { appId: '' };

    try {
      const response = await fetch('/config/intercom.config.json');
      if (!response.ok) {
        return defaultIntercomConfig;
      }

      cachedIntercomConfig = await response.json();
      return cachedIntercomConfig!;
    } catch {
      return defaultIntercomConfig;
    }
  },

  clearCache(): void {
    cachedFeedbackCollectorConfig = null;
    cachedIntercomConfig = null;
  },
};
